
import React, { useState } from 'react';

const PriceBreakdown = ({ formData }) => {
    const [isOpen, setIsOpen] = useState(false);

    const products = formData?.products || [];
    const soSchedule = formData?.soSchedule || [];

    const formatCurrency = (value) =>
        `₹ ${Number(value || 0).toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })}`;

    // Calculate total value for each SO
    const getSOValue = (so) => {
        if (!so?.productQuantities) return 0;

        return products.reduce((total, product) => {
            const quantity = Number(
                so.productQuantities?.[product.productCode] || 0
            );

            const unitPrice = Number(product.unitPrice || 0);

            return total + quantity * unitPrice;
        }, 0);
    };

    const soValues = soSchedule.map((so) => ({
        ...so,
        soValue: getSOValue(so),
    }));

    // Total PO Value
    const totalPOValue = soValues.reduce(
        (total, so) => total + so.soValue,
        0
    );

    // Existing total shipping
    const totalShipping = Number(formData?.shippingCost || 0);

    // Shipping split based on SO value
    const getSOShipping = (soValue) => {
        if (totalPOValue === 0) return 0;

        return (soValue / totalPOValue) * totalShipping;
    };

    // Grand Total
    const grandTotal = totalPOValue + totalShipping;

    const styles = {
        container: {
            marginTop: '24px',
            border: '1px solid #e5e7eb',
            borderRadius: '10px',
            overflow: 'hidden',
            backgroundColor: '#ffffff',
        },

        header: {
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 30px',
            border: 'none',
            backgroundColor: '#f8f9fa',
            cursor: 'pointer',
            fontSize: '18px',
            fontWeight: '600',
            color: '#222222',
            textAlign: 'left',
        },

        arrow: {
            fontSize: '20px',
            color: '#666666',
        },

        content: {
            padding: '35px 30px',
        },

        mainRow: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px 0',
            fontSize: '16px',
        },

        mainLabel: {
            fontWeight: '500',
            color: '#333333',
        },

        mainValue: {
            fontWeight: '600',
            color: '#222222',
        },

        divider: {
            borderTop: '1px solid #e1e5e9',
            margin: '25px 0',
        },

        sectionTitle: {
            fontSize: '17px',
            fontWeight: '500',
            color: '#333333',
            marginBottom: '18px',
        },

        soBlock: {
            marginBottom: '30px',
        },

        soTitle: {
            fontSize: '16px',
            fontWeight: '600',
            color: '#333333',
            marginBottom: '12px',
        },

        // Product table header
        productHeader: {
            display: 'grid',
            gridTemplateColumns: '1fr 120px 150px 180px',
            alignItems: 'center',
            padding: '0 25px 10px 25px',
            fontSize: '13px',
            fontWeight: '600',
            color: '#777777',
            borderBottom: '1px solid #eeeeee',
        },

        // Product row
        productRow: {
            display: 'grid',
            gridTemplateColumns: '1fr 120px 150px 180px',
            alignItems: 'center',
            padding: '9px 25px',
            fontSize: '14px',
        },

        productName: {
            color: '#444444',
            wordBreak: 'break-word',
        },

        quantity: {
            textAlign: 'right',
            color: '#555555',
            whiteSpace: 'nowrap',
        },

        unitPrice: {
            textAlign: 'right',
            color: '#555555',
            whiteSpace: 'nowrap',
        },

        productAmount: {
            textAlign: 'right',
            color: '#333333',
            fontWeight: '500',
            whiteSpace: 'nowrap',
        },

        soTotalContainer: {
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: '30px',
            marginTop: '8px',
            padding: '12px 25px 0 25px',
            borderTop: '1px solid #e1e5e9',
        },

        soTotalLabel: {
            fontSize: '14px',
            fontWeight: '600',
            color: '#333333',
        },

        soTotalValue: {
            width: '180px',
            textAlign: 'right',
            fontSize: '14px',
            fontWeight: '600',
            color: '#222222',
        },

        shippingRow: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 25px',
            fontSize: '14px',
            color: '#444444',
        },

        shippingValue: {
            minWidth: '180px',
            textAlign: 'right',
            fontWeight: '500',
        },

        totalRow: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 0',
            fontSize: '16px',
        },

        totalLabel: {
            fontWeight: '600',
            color: '#333333',
        },

        totalValue: {
            fontWeight: '600',
            color: '#222222',
        },

        grandTotal: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '15px 0',
            fontSize: '18px',
            fontWeight: '700',
            color: '#111111',
        },
    };

    return (
        <div style={styles.container}>
            {/* Accordion Header */}
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                style={styles.header}
                aria-expanded={isOpen}
            >
                <span>Price Breakdown</span>

                <span style={styles.arrow}>
                    {isOpen ? '−' : '+'}
                </span>
            </button>

            {isOpen && (
                <div style={styles.content}>
                    {/* PO VALUE */}
                    <div style={styles.mainRow}>
                        <span style={styles.mainLabel}>
                            PO Value
                        </span>

                        <span style={styles.mainValue}>
                            {formatCurrency(totalPOValue)}
                        </span>
                    </div>

                    <div style={styles.divider} />

                    {/* SHIPMENT */}
                    <div>
                        <div style={styles.sectionTitle}>
                            Shipment
                        </div>

                        {soSchedule.map((so, soIndex) => {
                            const soValue = getSOValue(so);

                            return (
                                <div
                                    key={soIndex}
                                    style={styles.soBlock}
                                >
                                    {/* SO Title */}
                                    <div style={styles.soTitle}>
                                        SO {soIndex + 1}
                                    </div>

                                    {/* Product Header */}
                                    <div style={styles.productHeader}>
                                        <span>Product</span>

                                        <span style={{ textAlign: 'right' }}>
                                            Quantity
                                        </span>

                                        <span style={{ textAlign: 'right' }}>
                                            Unit Price
                                        </span>

                                        <span style={{ textAlign: 'right' }}>
                                            Amount
                                        </span>
                                    </div>

                                    {/* Products */}
                                    {products.map((product, productIndex) => {
                                        const quantity = Number(
                                            so.productQuantities?.[
                                                product.productCode
                                            ] || 0
                                        );

                                        // Hide products with zero quantity
                                        if (quantity === 0) {
                                            return null;
                                        }

                                        const unitPrice = Number(
                                            product.unitPrice || 0
                                        );

                                        const amount = quantity * unitPrice;

                                        return (
                                            <div
                                                key={productIndex}
                                                style={styles.productRow}
                                            >
                                                {/* Product Name */}
                                                <span style={styles.productName}>
                                                    {product.productName ||
                                                        product.productCode}
                                                </span>

                                                {/* Quantity */}
                                                <span style={styles.quantity}>
                                                    ×{' '}
                                                    {quantity.toLocaleString(
                                                        'en-IN'
                                                    )}
                                                </span>

                                                {/* Unit Price */}
                                                <span style={styles.unitPrice}>
                                                    {formatCurrency(unitPrice)}
                                                </span>

                                                {/* Amount */}
                                                <span style={styles.productAmount}>
                                                    {formatCurrency(amount)}
                                                </span>
                                            </div>
                                        );
                                    })}

                                    {/* SO Total */}
                                    <div style={styles.soTotalContainer}>
                                        <span style={styles.soTotalLabel}>
                                            SO {soIndex + 1} Total
                                        </span>

                                        <span style={styles.soTotalValue}>
                                            {formatCurrency(soValue)}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div style={styles.divider} />

                    {/* SHIPPING CHARGES */}
                    <div>
                        <div style={styles.sectionTitle}>
                            Shipping Charges
                        </div>

                        {soValues.map((so, index) => {
                            const shipping = getSOShipping(so.soValue);

                            return (
                                <div
                                    key={index}
                                    style={styles.shippingRow}
                                >
                                    <span>
                                        SO {index + 1}
                                    </span>

                                    <span style={styles.shippingValue}>
                                        {formatCurrency(shipping)}
                                    </span>
                                </div>
                            );
                        })}
                    </div>

                    <div style={styles.divider} />

                    {/* TOTAL PO VALUE */}
                    <div style={styles.totalRow}>
                        <span style={styles.totalLabel}>
                            Total PO Value
                        </span>

                        <span style={styles.totalValue}>
                            {formatCurrency(totalPOValue)}
                        </span>
                    </div>

                    {/* TOTAL SHIPPING */}
                    <div style={styles.totalRow}>
                        <span style={styles.totalLabel}>
                            Total Shipping
                        </span>

                        <span style={styles.totalValue}>
                            {formatCurrency(totalShipping)}
                        </span>
                    </div>

                    <div style={styles.divider} />

                    {/* GRAND TOTAL */}
                    <div style={styles.grandTotal}>
                        <span>Grand Total</span>

                        <span>
                            {formatCurrency(grandTotal)}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PriceBreakdown;
